import { useState, useCallback, useEffect } from "react";
import { apiGet, apiPost } from "../lib/api";
import { getTeamStartMonth, getTeamMonthCount, getMonthsFrom, shiftMonth } from "../lib/dates";
import { ALL_BLOCKS, isPartialDay } from "../lib/blocks";
import { BRAND } from "../lib/brand";
import { FirstRun } from "../../../shell/FirstRun";
import { SmallLogo } from "../components/Logo";
import { MonthNavigator } from "../components/MonthNavigator";
import { CalendarMonth } from "../components/CalendarMonth";
import { TimeBlockPicker } from "../components/TimeBlockPicker";

/* ───────────────────── PARENT TRACKER VIEW ───────────────────── */
export function ParentTracker({ team, token, playerName, onBack, onLogout }) {
  // unavailDates: { "2025-04-12": ["08","10",...], ... }
  const [unavailDates, setUnavailDates] = useState({});
  const [pickerDate, setPickerDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const teamStart = getTeamStartMonth(team);
  const teamCount = getTeamMonthCount(team);
  const [startYear, setStartYear] = useState(teamStart.year);
  const [startMonth, setStartMonth] = useState(teamStart.month);
  const visibleMonths = getMonthsFrom(startYear, startMonth, teamCount);

  const handleShift = (delta) => {
    const s = shiftMonth(startYear, startMonth, delta);
    setStartYear(s.year);
    setStartMonth(s.month);
  };
  const handleReset = () => {
    setStartYear(teamStart.year);
    setStartMonth(teamStart.month);
  };

  useEffect(() => {
    apiGet("/availability/" + team.id, token)
      .then((data) => {
        const raw = data.players?.[playerName];
        if (!raw) { setLoading(false); return; }
        // New format: { date: blocksString, ... }
        if (typeof raw === "object" && !Array.isArray(raw)) {
          const parsed = {};
          Object.entries(raw).forEach(([d, b]) => {
            parsed[d] = typeof b === "string" ? b.split(",").filter(Boolean) : [...ALL_BLOCKS];
          });
          setUnavailDates(parsed);
        } else if (Array.isArray(raw)) {
          // Legacy: array of date strings
          const parsed = {};
          raw.forEach((d) => { parsed[d] = [...ALL_BLOCKS]; });
          setUnavailDates(parsed);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [team.id, token, playerName]);

  // One tap marks the WHOLE day. That's the overwhelmingly common case — "we're
  // at a wedding, they can't make anything" — and it's what the instructions on
  // this screen have always promised. Opening the picker first made the common
  // case three taps and a modal, and left the day marked-but-empty if you
  // dismissed it. The picker is for refining a day that's already marked.
  const toggleDate = useCallback((dk) => {
    setSaved(false);
    if (unavailDates[dk]) {
      setPickerDate(dk);          // already marked — refine to part of the day, or clear it
      return;
    }
    setUnavailDates((prev) => ({ ...prev, [dk]: [...ALL_BLOCKS] }));
  }, [unavailDates]);

  // Every way out of the picker lands here — ✕, backdrop, Done, or picking
  // nothing at all. A day with zero blocks must not survive: it counts toward
  // "days marked", and on save the API reads an empty blocks string as "none
  // given" and stores the day as FULLY unavailable — the exact opposite of
  // what the parent left the picker having chosen.
  const closePicker = useCallback(() => {
    setUnavailDates((prev) => {
      const blocks = prev[pickerDate];
      if (!blocks || blocks.length > 0) return prev;
      const next = { ...prev };
      delete next[pickerDate];
      return next;
    });
    setPickerDate(null);
  }, [pickerDate]);

  const handleBlockChange = useCallback((dk, blocks) => {
    setUnavailDates((prev) => {
      if (!blocks || blocks.length === 0) {
        const next = { ...prev };
        delete next[dk];
        return next;
      }
      return { ...prev, [dk]: blocks };
    });
    setSaved(false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Belt and braces: a zero-block day is not an answer, and sending one
      // would come back as a full-day unavailable.
      const payload = Object.entries(unavailDates)
        .filter(([, blocks]) => blocks && blocks.length > 0)
        .map(([date, blocks]) => ({ date, blocks: blocks.join(",") }));
      await apiPost("/availability/" + team.id, { playerName, unavailableDates: payload }, token);
      setSaved(true);
    } catch (e) {
      alert("Failed to save: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const unavailCount = Object.keys(unavailDates).length;
  const partialCount = Object.values(unavailDates).filter((b) => isPartialDay(b)).length;
  // Zero marked days is a real answer ("available every day"), not "hasn't
  // looked at this yet" — see checkpoint_2026-07-28's design trap #1. The
  // explicit affirmative button only makes sense in this state; once anything
  // is marked, the ordinary Save button is the right one again.
  const zeroMarked = unavailCount === 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white flex items-center justify-center">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <SmallLogo />
            <div>
              <h1 className="text-xl font-extrabold leading-none" style={{ color: BRAND.accent }}>Sartell Soccer</h1>
              <p className="text-xs font-medium" style={{ color: BRAND.primary }}>{team.name} &middot; {playerName}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
              Switch Player
            </button>
            <button onClick={onLogout} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
              Logout
            </button>
          </div>
        </div>

        <div className="mb-4">
          {/* id bumped to _v2: the copy below now names the explicit
              "No conflicts" button, and anyone who dismissed the old wording
              needs to see that it exists. */}
          <FirstRun id="avail_tracker_v2" title="Marking your dates">
            <p>
              Tap the days <strong>{playerName}</strong> <em>can't</em> make it — one tap marks
              the whole day red. Tap a red day again if it's only part of that day, or to
              clear it.
            </p>
            <p>
              Then hit <strong>Save</strong>. Your coach sees it right away, and you can come
              back and change it any time.
            </p>
            <p>
              Available every day? Leave every day unmarked and hit{" "}
              <strong>"✓ No conflicts — available all season"</strong> below — that's a real
              answer, not a skip, and your coach sees it as one.
            </p>
          </FirstRun>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <p className="text-sm text-gray-600 text-center">
            Tap a date to mark <strong>{playerName}</strong> as <span className="font-semibold text-red-600">unavailable</span> for the full day.
            Tap a marked date again to pick specific time blocks, or to clear it.
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#dc2626"}} /> Full day</span>
            <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded" style={{backgroundColor:"#fbbf24"}} /> Partial</span>
            <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded border border-gray-200" /> Available</span>
          </div>
        </div>

        <MonthNavigator startYear={startYear} startMonth={startMonth} onShift={handleShift} onReset={handleReset} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {visibleMonths.map((m) => (
            <CalendarMonth key={m.name + m.year} month={m} unavailDates={unavailDates} onToggle={toggleDate} canEdit={true} />
          ))}
        </div>

        <div className="sticky bottom-4 flex flex-col items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 font-semibold rounded-2xl shadow-lg transition-all text-white"
            style={{ backgroundColor: zeroMarked || saved ? "#16a34a" : BRAND.primary }}
          >
            {saving
              ? "Saving..."
              // The save call is identical either way (zero picks + the
              // lastSubmittedAt stamp) — only the label and the confirmation
              // copy differ, so a parent who saves nothing gets an answer
              // that reads like one, not a shrug.
              : zeroMarked
              ? (saved ? "✓ Saved — available all season" : "✓ No conflicts — available all season")
              : (saved ? "Saved!" : "Save Availability (" + unavailCount + " days marked)")}
          </button>
          {saved && zeroMarked && (
            <p className="text-sm text-green-700 text-center max-w-md">
              Got it — <strong>{playerName}</strong> is marked available all season. You can
              come back and change this any time.
            </p>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          {unavailCount} day{unavailCount !== 1 ? "s" : ""} marked unavailable
          {partialCount > 0 && " (" + partialCount + " partial)"}
        </p>

        {pickerDate && (
          <TimeBlockPicker
            dateKey={pickerDate}
            blocks={unavailDates[pickerDate] || []}
            onChange={handleBlockChange}
            onClose={closePicker}
          />
        )}
      </div>
    </div>
  );
}
